import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { UsersPanel, BillingPanel } from "../../components/admin/UsersPanel";
import { staffApi, apiError } from "../../lib/staffApi";

const PasswordPanel = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    try {
      await staffApi.post("/password", { current_password: f.current_password, new_password: f.new_password });
      setF({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (ex) { toast.error(apiError(ex)); }
  };
  const field = (k, label, ac) => (
    <label className="block text-sm text-stone-600 space-y-1"><span>{label}</span>
      <Input data-testid={`staff-pw-${k}`} type="password" autoComplete={ac} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  return (
    <form onSubmit={save} className="scroll-card p-6 max-w-md space-y-4" data-testid="staff-password-panel">
      <h3 className="font-bold">Change password</h3>
      {field("current_password", "Current password", "current-password")}
      {field("new_password", "New password (min 8 characters)", "new-password")}
      {field("confirm", "Confirm new password", "new-password")}
      <button data-testid="staff-pw-save" className="h-10 px-5 rounded-full bg-[var(--vermilion)] text-white text-sm font-semibold">Update password</button>
    </form>
  );
};

export default function StaffDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => { staffApi.get("/me").then(({ data }) => setMe(data)).catch(() => nav("/staff/login", { replace: true })); }, [nav]);
  const logout = async () => { await staffApi.post("/logout").catch(() => {}); nav("/staff/login", { replace: true }); };
  if (!me) return <div className="min-h-screen paper-bg" data-testid="staff-loading" />;
  return (
    <div className="min-h-screen paper-bg font-en" data-testid="staff-dashboard">
      <header className="bg-[var(--ink)] text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="font-brush text-3xl text-amber-300">小書童</div>
          <div className="font-semibold">Admin Console</div>
          <div className="flex-1" />
          <span className="text-sm text-white/70 hidden sm:block" data-testid="staff-email">{me.email}</span>
          <button data-testid="staff-logout-btn" onClick={logout} className="h-9 px-4 rounded-full bg-white/10 text-sm flex items-center gap-2"><LogOut size={14} /> Sign out</button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-6">
        <Tabs defaultValue="users">
          <TabsList className="bg-amber-100">
            <TabsTrigger value="users" data-testid="staff-tab-users">Users</TabsTrigger>
            <TabsTrigger value="billing" data-testid="staff-tab-billing">Plans</TabsTrigger>
            <TabsTrigger value="account" data-testid="staff-tab-account">Account</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-6"><UsersPanel /></TabsContent>
          <TabsContent value="billing" className="mt-6"><BillingPanel /></TabsContent>
          <TabsContent value="account" className="mt-6"><PasswordPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
